import { Injectable } from '@angular/core';
import { FirebaseError } from '@angular/fire/app';

import {
  Auth,
  signOut,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  User,
  authState,
} from '@angular/fire/auth';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationFirebaseService {
  user$: Observable<User | null>;
  constructor(private auth: Auth) {
    this.user$ = authState(this.auth);
  }

  //#region Core
  async getUser(): Promise<User> {
    return await this.user$.pipe(take(1)).toPromise();
  }
  //#endregion

  //#region Authentication
  /**
   * Registers a new user
   *
   * @param email The email value
   * @param pwd The wished password
   * @returns The user registered
   */
  async registerEmailPwd(displayName: string, email: string, pwd: string) {
    return new Promise<void>(async (resolve, reject) => {
      console.log('registering user');
      await createUserWithEmailAndPassword(this.auth, email, pwd)
        .then(async () => {
          console.log('user created');
          await updateProfile(this.auth.currentUser, { displayName })
            .then(() => {
              console.log('profile updated');
              this.computeUserData();
              resolve();
            })
            .catch((error) => {
              console.error('error updating profile : ', error);
              reject(error);
            });
        })
        .catch((reason) => {
          console.error('error updating : ', reason);
          localStorage.removeItem('user');
          reject(reason);
        });
    });
  }

  /**
   * Login the user with email and password
   *
   * @param email The email value
   * @param pwd The wished password
   * @returns The recently logged user
   */
  async loginEmailPwd(email: string, pwd: string) {
    return await signInWithEmailAndPassword(this.auth, email, pwd)
      .then(() => {
        this.computeUserData();
      })
      .catch((reason: FirebaseError) => {
        console.log('error loging in : ', reason);
        localStorage.removeItem('user');
        throw reason;
      });
  }

  /**
   * Resets the user with email
   *
   * @param email The email value
   * @returns True if the email has been set
   */
  async passwordForgotten(email: string) {
    return new Promise<boolean>(async (resolve, reject) => {
      await sendPasswordResetEmail(this.auth, email)
        .then(() => {
          resolve(true);
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  /**
   * Function to log onto the app with google
   *
   * @returns "UserCredential" object if login successful, false if any error happen
   */
  async loginWithGoogle() {
    const google = new GoogleAuthProvider();
    return await signInWithPopup(this.auth, google)
      .then(() => {
        this.computeUserData();
      })
      .catch((reason) => {
        localStorage.removeItem('user');
        console.log('error registering / logging in with google : ', reason);
      });
  }

  /**
   * Fonction that logs out the user
   *
   * @returns True if the user is successfully displogged from the app, false otherwise
   */
  async logout() {
    return await signOut(this.auth)
      .then(() => {
        console.log('the user was logged out');
        localStorage.removeItem('user');
      })
      .catch((reason) => {
        console.error('error logging out : ', reason);
      });
  }

  //#endregion Authentication

  //#region Private methods
  /**
   *
   * Function to get the current user data
   *
   * @returns The data of the user logged in
   */
  private computeUserData() {
    const userData = this.auth.currentUser.providerData[0];
    localStorage.setItem('user', JSON.stringify(userData));
  }
  //#endregion Private methods
}
